// src/hooks/useTasks.js
import { useState, useEffect, useCallback } from "react";
import {
  collection, onSnapshot, doc, addDoc, deleteDoc, updateDoc,
  writeBatch, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { matchAssignmentRule } from "../lib/assignmentRules";

function tasksCol(orgId) {
  return collection(db, "organizations", orgId, "tasks");
}

function taskDoc(orgId, taskId) {
  return doc(db, "organizations", orgId, "tasks", taskId);
}

function postDoc(orgId, postId) {
  return doc(db, "organizations", orgId, "pinchpoints", postId);
}

export function useTasks(orgId) {
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(tasksCol(orgId), (snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setTasks(all);
    });
  }, [orgId]);

  const createTask = useCallback(async ({ title, description, assignedTo, assignedToName, assignedTo2, assignedToName2, postIds, category, severity, rules }) => {
    let finalTo = assignedTo || null;
    let finalToName = assignedToName || null;
    if (!finalTo && rules?.length) {
      const matched = matchAssignmentRule(rules, category, severity);
      if (matched) { finalTo = matched.assignTo; finalToName = matched.assignToName; }
    }
    await addDoc(tasksCol(orgId), {
      title,
      description: description || "",
      assignedTo: finalTo,
      assignedToName: finalToName,
      assignedTo2: assignedTo2 || null,
      assignedToName2: assignedToName2 || null,
      postIds: postIds || [],
      status: "open",
      accepted: finalTo ? false : null,
      accepted2: assignedTo2 ? false : null,
      ...(category ? { category } : {}),
      ...(severity ? { severity } : {}),
      createdAt: serverTimestamp(),
    });
  }, [orgId]);

  const assignTask = useCallback(async (taskId, assignedTo, assignedToName) => {
    await updateDoc(taskDoc(orgId, taskId), { assignedTo, assignedToName, accepted: false });
    try {
      await addDoc(collection(db, "organizations", orgId, "tasks", taskId, "history"), {
        event: "assigned",
        assignedTo: assignedTo || null,
        assignedToName: assignedToName || null,
        timestamp: serverTimestamp(),
      });
    } catch (e) {
      console.error("Task history write failed:", e);
    }
  }, [orgId]);

  // Assign / clear a secondary (shift-change) assignee independently of the primary.
  const assignSecondaryTask = useCallback(async (taskId, assignedTo2, assignedToName2) => {
    await updateDoc(taskDoc(orgId, taskId), { assignedTo2, assignedToName2, accepted2: false });
  }, [orgId]);

  const acceptTask = useCallback(async (taskId) => {
    await updateDoc(taskDoc(orgId, taskId), { accepted: true });
  }, [orgId]);

  const acceptSecondaryTask = useCallback(async (taskId) => {
    await updateDoc(taskDoc(orgId, taskId), { accepted2: true });
  }, [orgId]);

  const denyTask = useCallback(async (taskId) => {
    await updateDoc(taskDoc(orgId, taskId), { assignedTo: null, assignedToName: null, accepted: null });
  }, [orgId]);

  const denySecondaryTask = useCallback(async (taskId) => {
    await updateDoc(taskDoc(orgId, taskId), { assignedTo2: null, assignedToName2: null, accepted2: null });
  }, [orgId]);

  const completeTask = useCallback(async (taskId, postIds, closingNote = null) => {
    const batch = writeBatch(db);
    for (const postId of postIds) {
      batch.update(postDoc(orgId, postId), {
        resolved: true,
        status: "resolved",
        resolvedAt: serverTimestamp(),
        resolvedByTask: taskId,
      });
      const histRef = doc(collection(db, "organizations", orgId, "pinchpoints", postId, "statusHistory"));
      batch.set(histRef, {
        toStatus: "resolved",
        note: closingNote || `Resolved via task ${taskId}`,
        reason: null,
        actorUid: null,
        actorName: "Task System",
        changedAt: serverTimestamp(),
      });
    }
    const taskUpdate = { status: "completed", completedAt: serverTimestamp() };
    if (closingNote) taskUpdate.closingNote = closingNote;
    batch.update(taskDoc(orgId, taskId), taskUpdate);
    await batch.commit();

    try {
      await addDoc(collection(db, "organizations", orgId, "tasks", taskId, "history"), {
        event: "completed",
        note: closingNote || null,
        timestamp: serverTimestamp(),
      });
    } catch (e) {
      console.error("Task history write failed:", e);
    }
  }, [orgId]);

  const deleteTask = useCallback(async (taskId) => {
    await deleteDoc(taskDoc(orgId, taskId));
  }, [orgId]);

  const createDraftTasks = useCallback(async (tasksArray) => {
    await Promise.all(tasksArray.map((t) =>
      addDoc(tasksCol(orgId), {
        title: t.title,
        description: t.description || "",
        assignedTo: null,
        assignedToName: null,
        postIds: t.postIds || [],
        status: "draft",
        accepted: null,
        createdAt: serverTimestamp(),
      })
    ));
  }, [orgId]);

  const approveDraftTask = useCallback(async (taskId, title, description, opts = {}) => {
    const update = { status: "open", title, description };
    if (!opts.keepAssignee && opts.rules?.length) {
      const matched = matchAssignmentRule(opts.rules, opts.category, opts.severity);
      if (matched) {
        update.assignedTo = matched.assignTo;
        update.assignedToName = matched.assignToName;
        update.accepted = false;
      }
    }
    await updateDoc(taskDoc(orgId, taskId), update);
  }, [orgId]);

  return { tasks, createTask, assignTask, assignSecondaryTask, acceptTask, acceptSecondaryTask, denyTask, denySecondaryTask, completeTask, deleteTask, createDraftTasks, approveDraftTask };
}
